import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/** Shape of GET /api/webhooks/durranitech/key (api-durranitech-webhook.js). */
export interface DurranitechApiKeyStatus {
  configured: boolean;
  apiKey: string | null;
}

export interface RotateDurranitechApiKeyResult {
  message: string;
  apiKey: string;
}

/** Current webhook key status — used by DurranitechWebhookCard. */
export function useDurranitechApiKey() {
  return useQuery({
    queryKey: ['durranitech-api-key'],
    queryFn: async () => {
      const { data } = await api.get<DurranitechApiKeyStatus>('/api/webhooks/durranitech/key');
      return data;
    },
  });
}

/** Generate or rotate the webhook key. Optionally supply your own value. */
export function useRotateDurranitechApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    // Takes the optional custom key directly, which is how the card calls it.
    mutationFn: async (customKey?: string) => {
      const { data } = await api.post<RotateDurranitechApiKeyResult>(
        '/api/webhooks/durranitech/rotate-key',
        customKey ? { customKey } : {},
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['durranitech-api-key'] }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: ChangePasswordPayload) => {
      const { data } = await api.post<{ ok: boolean }>('/api/auth/change-password', payload);
      return data;
    },
  });
}
